import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api'; // Assuming BASE_URL is correctly defined
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf'; // Import jsPDF
import 'jspdf-autotable'; // Import for table support
import * as XLSX from 'xlsx'; // Import xlsx for Excel export

const CustomerList = () => {
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetch(`${BASE_URL}customers`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setCustomers(data.data);
                    setFilteredCustomers(data.data); // Set initial filtered customers
                } else {
                    console.error('Failed to fetch customers:', data.message);
                }
            })
            .catch(error => console.error('Error fetching customers:', error));
    }, []);

    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);

        // Filter customers based on the search term
        const filtered = customers.filter(person =>
            person.name.toLowerCase().includes(term.toLowerCase()) ||
            person.number1.toLowerCase().includes(term.toLowerCase()) ||
            (person.address && person.address.toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredCustomers(filtered);
    };

    const handleEdit = (id) => {
        navigate(`/EditCustomer/${id}`); // Navigate to the Edit Customer page
    };

    const handleView = (id) => {
        navigate(`/ViewCustomer/${id}`); // Navigate to the View Customer page
    };

    const handleAdd = () => {
        navigate('/AddCustomer');
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this customer?")) {
            fetch(`${BASE_URL}customers/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(response => response.json())
                .then(data => {
                    if (data.status === 1) {
                        setCustomers(customers.filter(person => person.id !== id));
                        setFilteredCustomers(filteredCustomers.filter(person => person.id !== id));
                    }
                })
                .catch(error => console.error('Error deleting customer:', error));
        }
    };

    // Export filteredCustomers to PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Add Title
        doc.text('Customer List', 14, 10); // Title at top of PDF

        // Prepare table data for PDF
        const tableData = filteredCustomers.map(person => [
            person.name || 'N/A',
            person.number1 || 'N/A',
            person.address || 'N/A',
            person.area || 'N/A',
            person.city || 'N/A',
            person.state || 'N/A',
            person.pin_code || 'N/A',
        ]);

        // Add table to PDF with autoTable
        doc.autoTable({
            head: [['Name', 'Phone', 'Address', 'Area', 'City', 'State', 'Pin Code']], // Table headers
            body: tableData,
        });

        // Save the PDF with a filename
        doc.save('customer_list.pdf');
    };

    // Export filteredCustomers to Excel
    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredCustomers.map(person => ({
            Name: person.name || 'N/A',
            Phone: person.number1 || 'N/A',
            Address: person.address || 'N/A',
            Area: person.area || 'N/A',
            City: person.city || 'N/A',
            State: person.state || 'N/A',
            'Pin Code': person.pin_code || 'N/A',
        })));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Customer List");

        // Save the Excel file with a filename
        XLSX.writeFile(workbook, 'customer_list.xlsx');
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Customer List</h3>
                        </div>
                    </div>
                </div>
            </div>

            <section className="section">
                <div className="row">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-md-6">
                                    </div>
                                    <div className="col-md-6 text-end">
                                        <button className="btn btn-primary" onClick={handleAdd}>Add Customer</button>

                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label htmlFor="searchTerm" className="form-label">Search by Name, Phone, or Address</label>
                                    <input
                                        type="text"
                                        id="searchTerm"
                                        className="form-control"
                                        placeholder="Search customer name, phone, or address"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>NAME</th>
                                                <th>PHONE</th>
                                                <th>ADDRESS</th>
                                                <th>AREA</th>
                                                <th>CITY</th>
                                                <th>STATE</th>
                                                <th>PIN CODE</th>
                                                <th className="text-end">ACTIONS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCustomers.length > 0 ? (
                                                filteredCustomers.map(person => (
                                                    <tr key={person.id}>
                                                        <td>{person.name || 'N/A'}</td>
                                                        <td>{person.number1 || 'N/A'}</td>
                                                        <td>{person.address || 'N/A'}</td>
                                                        <td>{person.area || 'N/A'}</td>
                                                        <td>{person.city || 'N/A'}</td>
                                                        <td>{person.state || 'N/A'}</td>
                                                        <td>{person.pin_code || 'N/A'}</td>
                                                        <td className="text-end">
                                                            <button className="btn btn-success" onClick={() => handleEdit(person.id)}>Edit</button>
                                                            <button className="btn btn-info ms-2" onClick={() => handleView(person.id)}>View</button>
                                                            <button className="btn btn-danger ms-2" onClick={() => handleDelete(person.id)}>Delete</button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center">No customers found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CustomerList;
